/*
 * Quick view bridge.
 *
 * The "+" on a product card does not reimplement quick add. Dawn already ships
 * the whole modal (quick-add.js + quick-add-modal), it just renders it as a
 * full-width button under the card. That button is hidden in CSS and the "+"
 * forwards its click, so Dawn keeps owning variant pickers, price and cart.
 *
 * Deliberately separate from rc-wishlist.js: the built-in wishlist can be turned
 * off (settings.rc_wishlist_enabled) once a wishlist app takes over, and quick
 * view must keep working when it is.
 */
(function () {
  document.addEventListener('click', function (event) {
    const trigger = event.target.closest('[data-rc-quickview]');
    if (!trigger) return;
    const card = trigger.closest('.card-wrapper');
    if (!card) return;
    const dawnButton = card.querySelector('.quick-add__submit');
    if (!dawnButton) return;
    event.preventDefault();
    event.stopPropagation();
    dawnButton.click();
  });

  /* ------------------------------------------------- gallery arrows (desktop)
   *
   * At desktop widths the quick view's gallery is NOT a slider: the media list
   * has `slider--mobile`, so above 750px it stops scrolling, every media item
   * but the active one collapses to zero size, and Dawn's own
   * `.slider-button--prev/next` are hidden AND inert — clicking them changes
   * nothing. What actually moves the gallery there is the thumbnail list, which
   * our CSS shrinks to dots under the image.
   *
   * So these arrows don't try to drive the slider. They click the neighbouring
   * thumbnail, which is exactly what a shopper does today, and let Dawn's
   * media-gallery do the switching. Index wraps, so the arrows never dead-end on
   * a 4-image gallery inside a modal.
   *
   * Mobile is left alone — there the list is a real peek slider and swipes.
   */
  const NAV = 'rc-qv-nav';

  function arrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = NAV + ' ' + NAV + '--' + direction;
    button.dataset.rcQvNav = direction;
    button.setAttribute('aria-label', direction === 'next' ? 'Next image' : 'Previous image');
    button.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path d="' +
      (direction === 'next' ? 'm9 5 7 7-7 7' : 'm15 5-7 7 7 7') +
      '" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    return button;
  }

  function thumbnailsIn(modal) {
    return Array.prototype.slice.call(modal.querySelectorAll('.thumbnail-list__item .thumbnail'));
  }

  /* The arrows are inset from the gallery viewer, which spans the whole media
     column, while the picture inside it is only as wide as its aspect allows at
     the modal's capped height — leaving a gutter that put both arrows beside
     the frame rather than on it.

     That gutter is not a constant: it moves with the media's aspect ratio and
     the viewport. Hardcoding it is the mistake already made and undone on the
     PDP gallery heart, and `width: fit-content` on the viewer is circular (the
     media list sizes from its container, so it collapses the picture to 2px).
     So the offset is measured here and published as a custom property for the
     stylesheet to add to its own 1rem edge inset. */
  function positionArrows(modal) {
    const container = modal.querySelector('slider-component:not(.thumbnail-slider)');
    if (!container) return;

    /* Prefer the item Dawn marks active. On desktop the inactive items are
       display:none and measure 0x0, so "first item" alone would pick a
       collapsed box and compute a nonsense inset. */
    const picture =
      modal.querySelector('.product__media-item.is-active .product-media-container') ||
      Array.prototype.slice
        .call(modal.querySelectorAll('.product-media-container'))
        .find(function (el) {
          return el.getBoundingClientRect().width > 0;
        });
    if (!picture) return;

    const gutter = (container.getBoundingClientRect().width - picture.getBoundingClientRect().width) / 2;
    container.style.setProperty('--rc-qv-inset', Math.max(0, Math.round(gutter)) + 'px');
  }

  function ensureArrows(modal) {
    /* Anchor to the gallery viewer, NOT to .product-media-container: that one is
       rendered per media item, so arrows appended to it live inside slide 1 and
       disappear the moment the gallery moves to slide 2 (every inactive item
       collapses to zero size). The viewer wraps all of them and stays put. */
    const container = modal.querySelector('slider-component:not(.thumbnail-slider)');
    /* One image needs no navigation, and a container that already has the
       arrows must not collect a second pair when the modal re-renders. */
    if (!container || thumbnailsIn(modal).length < 2) return;
    if (container.querySelector('.' + NAV)) {
      positionArrows(modal);
      return;
    }
    container.appendChild(arrow('prev'));
    container.appendChild(arrow('next'));
    positionArrows(modal);

    /* The picture settles after its image decodes, and switching media does not
       fire a resize — so re-measure on the box actually changing, not just on
       window resize. Same belt-and-braces as the PDP heart. */
    if (typeof ResizeObserver === 'function' && !container.dataset.rcQvObserved) {
      container.dataset.rcQvObserved = 'true';
      new ResizeObserver(function () {
        positionArrows(modal);
      }).observe(container);
    }
  }

  /* Moving the gallery means clicking the neighbouring thumbnail and letting
     Dawn's media-gallery do the work — the modal's media list is not a real
     scroller above 750px, so there is nothing to scroll. Shared by the arrows
     and by swipe. */
  function navigate(modal, step) {
    const thumbs = thumbnailsIn(modal);
    if (thumbs.length < 2) return;
    let index = thumbs.findIndex(function (t) {
      return t.getAttribute('aria-current') === 'true';
    });
    if (index < 0) index = 0;
    thumbs[(index + step + thumbs.length) % thumbs.length].click();
  }

  document.addEventListener('click', function (event) {
    const nav = event.target.closest('.' + NAV);
    if (!nav) return;
    event.preventDefault();
    event.stopPropagation();

    const modal = nav.closest('quick-add-modal');
    if (!modal) return;
    navigate(modal, nav.dataset.rcQvNav === 'next' ? 1 : -1);
  });

  /* --- swipe -------------------------------------------------------------
     Dawn's gallery is swipeable on mobile only because it is a peek SLIDER
     there; inside the modal that slider is neutralised (one media item shown,
     the peek and gutter removed), so there was nothing to swipe and the dots
     were the only way through the images.

     Listeners are delegated on the document so modals added later — the
     lazy-fetched "You may also like" section — are covered without rebinding,
     the same trap that left those modals without arrows.

     Both listeners are PASSIVE and never call preventDefault: the gesture is
     classified only after it finishes, so a vertical drag scrolls the modal
     exactly as before. A horizontal move only counts when it is over 40px AND
     longer than the vertical move, which is what stops a slightly-diagonal
     scroll from flicking the image. */
  const SWIPE_MIN = 40;
  let startX = 0;
  let startY = 0;
  let swipeModal = null;

  document.addEventListener(
    'touchstart',
    function (event) {
      swipeModal = null;
      if (event.touches.length !== 1) return; // ignore pinch-zoom
      const target = event.target;
      if (!target || !target.closest) return;
      const modal = target.closest('quick-add-modal[open]');
      if (!modal || !target.closest('.product__media-wrapper')) return;
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      swipeModal = modal;
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    function (event) {
      const modal = swipeModal;
      swipeModal = null;
      if (!modal || !event.changedTouches || event.changedTouches.length !== 1) return;
      const dx = event.changedTouches[0].clientX - startX;
      const dy = event.changedTouches[0].clientY - startY;
      if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
      navigate(modal, dx < 0 ? 1 : -1);
    },
    { passive: true }
  );

  /* Dawn fills the modal by setting innerHTML when it opens, so the arrows have
     to be added after that rather than once at page load.

     🔑 The observer watches the DOCUMENT, not each quick-add-modal found at
     load. Observing the modals that happen to exist on DOMContentLoaded misses
     every modal added later — and "You may also like" is exactly that: the
     recommendations section lazy-fetches when scrolled into view, so its
     modals were never observed and never got arrows, while the collection grid
     and homepage (whose modals are in the initial HTML) worked. That is the
     "some modals have no navigation" report, and it is why a sweep of the
     collection page alone passed 16/16 and still missed it.

     Appending the arrows mutates childList and re-enters this callback, so the
     work is coalesced into one rAF; the second pass finds the arrows already
     there and early-returns. positionArrows only writes a custom property,
     which is an attribute change and is not watched here, so it cannot loop. */
  let scheduled = false;

  function refreshAll() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      document.querySelectorAll('quick-add-modal').forEach(ensureArrows);
    });
  }

  const observer = new MutationObserver(refreshAll);

  function watch() {
    observer.observe(document.documentElement, { childList: true, subtree: true });
    refreshAll();
  }

  window.addEventListener('resize', function () {
    document.querySelectorAll('quick-add-modal[open]').forEach(positionArrows);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
